import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import api, { clearAuthTokens } from '../lib/api';

const AuthContext = createContext(null);

// Mirror the active user_id into localStorage so non-React code (axios
// interceptor in lib/api.js) can pick the correct per-user factory key.
// Without this, two users sharing a device end up sending each other's
// X-Factory-Id header → "same login, different data" cross-device bug.
const stampUid = (u) => {
  try {
    if (u && u.user_id) localStorage.setItem('c2p_active_uid', u.user_id);
    else localStorage.removeItem('c2p_active_uid');
  } catch (e) { console.debug('[caught]', e); }
};

export function AuthProvider({ children }) {
  const [user, _setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((u) => {
    stampUid(u);
    // Avoid spurious reference changes when /auth/me returns the same user.
    // A new object identity here would cascade re-renders to every consumer
    // even when nothing actually changed.
    _setUser((prev) => {
      if (prev && u && prev.user_id === u.user_id && prev.role === u.role) {
        // Shallow-compare a few cheap fields. Skip deep diffing — the
        // intent is just to dampen visual flicker on focus revalidate.
        const same =
          prev.email === u.email &&
          prev.name === u.name &&
          (prev.factory_id || '') === (u.factory_id || '') &&
          (prev.company_id || '') === (u.company_id || '');
        if (same) return prev;
      }
      return u;
    });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      // Only treat hard 401 (invalid/expired) as logout.
      // Network errors, 5xx, etc. keep the current user in place — no false logouts.
      if (err?.response?.status === 401) {
        _setUser(null);
        stampUid(null);
      }
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Silent-refresh heartbeat: re-validate auth whenever the tab regains focus.
  // If the access token has expired in the background, the axios interceptor
  // will auto-refresh using the 7-day refresh cookie. This prevents "random
  // logouts" when a user returns to a tab after lunch/overnight.
  //
  // iter102 — anti-flicker: the previous version called `checkAuth` on EVERY
  // window focus event. On mobile, soft-keyboard open/close, address-bar
  // taps and notification overlays all fire focus, causing /auth/me to be
  // refetched many times per minute. Each response did `setUser(res.data)`
  // with a fresh object reference, which cascaded re-renders into
  // TopBar (loadCounts), ProtectedRoute, and every page consuming `user`.
  //
  // New rules:
  //   • Listen to `visibilitychange` instead of `focus` — visibility flips
  //     less often and only on real tab/app switches.
  //   • Throttle: skip the check if the document was hidden < 5 minutes.
  //   • Background poll dropped from 10m → 30m (refresh cookie is 7d, so
  //     this is mostly a safety net).
  useEffect(() => {
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      // Just became visible. Only re-check if we were really gone.
      if (hiddenAt && Date.now() - hiddenAt >= 5 * 60 * 1000) {
        checkAuth();
      }
      hiddenAt = 0;
    };
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(checkAuth, 30 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [checkAuth]);

  const loginWithPin = useCallback(async (phone, pin) => {
    const res = await api.post('/auth/phone-login', { phone, pin });
    setUser(res.data);
    return res.data;
  }, [setUser]);

  const setPin = useCallback(async (phone, currentPin, newPin) => {
    const res = await api.post('/auth/set-pin', { phone, current_pin: currentPin, new_pin: newPin });
    setUser(res.data);
    return res.data;
  }, [setUser]);

  const checkPhone = useCallback(async (phone) => {
    const res = await api.post('/auth/phone-check', { phone });
    return res.data;
  }, []);

  // Legacy login kept for backward compat (admin tools)
  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data);
    return res.data;
  }, [setUser]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (e) { console.debug('[caught]', e); }
    clearAuthTokens();
    setUser(null);
  }, [setUser]);

  // Memoise the context value so consumers only re-render when `user`/`loading`
  // actually change — not on every AuthProvider render. All handlers above are
  // stable (useCallback), so the object identity is stable too.
  const value = useMemo(
    () => ({ user, loading, login, loginWithPin, setPin, checkPhone, logout, checkAuth, setUser }),
    [user, loading, login, loginWithPin, setPin, checkPhone, logout, checkAuth, setUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
